import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FaLockOpen } from 'react-icons/fa';
import EditCancelModal from './Global/EditCancelModal';
import ConfirmModal from './Global/ConfirmModal';
import AddressAutocompleteInput from './Global/AddressAutocompleteInput';
import { createClientAddress, updateClientAddress, logError } from '../api';

const AUTOCOMPLETE_FIELDS = ['address', 'city', 'zipCode', 'state'];
const ALL_LOCKED = { address: true, city: true, zipCode: true, state: true };
const ALL_UNLOCKED = { address: false, city: false, zipCode: false, state: false };

const EMPTY_FORM = { address: '', addressDetails: '', city: '', district: '', state: '', zipCode: '', country: 'España', isDefault: false };

const AddAddressModal = ({ show, handleClose, handleSave, address, clientId }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [isDirty, setIsDirty] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [lockedFields, setLockedFields] = useState(ALL_UNLOCKED);
    const [unlockPending, setUnlockPending] = useState(false);

    useEffect(() => {
        if (address) {
            setForm({
                address: address.address ?? '',
                addressDetails: '',
                city: address.city ?? '',
                district: address.district ?? '',
                state: address.state ?? '',
                zipCode: address.zipCode ?? '',
                country: address.country ?? 'España',
                isDefault: address.isDefault ?? false,
            });
        } else {
            setForm(EMPTY_FORM);
        }
        setLockedFields(ALL_LOCKED);
        setSelectedSuggestion(null);
        setIsDirty(false);
    }, [address, show]);

    const handleSuggestionSelect = (s) => {
        const autoAddress = [s.street, s.houseNumber].filter(Boolean).join(' ');
        setSelectedSuggestion({ address: autoAddress, city: s.city ?? '', zipCode: s.postcode ?? '', state: s.state ?? '', latitude: s.latitude, longitude: s.longitude });
        setLockedFields(ALL_LOCKED);
        setForm(prev => ({ ...prev, address: autoAddress, city: s.city ?? prev.city, zipCode: s.postcode ?? prev.zipCode, state: s.state ?? prev.state }));
        setIsDirty(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setIsDirty(true);
    };

    const handleSubmit = () => {
        const geoMatches = selectedSuggestion && AUTOCOMPLETE_FIELDS.every(
            f => (form[f] ?? '').trim() === (selectedSuggestion[f] ?? '').trim()
        );

        const updatedAddress = {
            address: [form.address, form.addressDetails].filter(Boolean).join(', '),
            city: form.city,
            district: form.district,
            state: form.state,
            zipCode: form.zipCode,
            country: form.country,
            isDefault: form.isDefault,
            clientId,
            ...(geoMatches && { latitude: selectedSuggestion.latitude, longitude: selectedSuggestion.longitude, exactGeoMatch: true }),
        };

        if (address?.id) {
            updatedAddress.id = address.id;
            updateClientAddress(address.id, updatedAddress)
                .then(() => { handleSave(updatedAddress); handleClose(); })
                .catch((error) => { logError(error); console.error('Error al actualizar la dirección:', error); });
        } else {
            createClientAddress(updatedAddress)
                .then((response) => { handleSave(response.data); handleClose(); })
                .catch((error) => { logError(error); console.error('Error al crear la dirección:', error); });
        }
    };

    const handleCancel = () => {
        if (isDirty) setShowCancelModal(true);
        else handleClose();
    };

    return (
        <>
            <ConfirmModal
                show={unlockPending}
                onCancel={() => setUnlockPending(false)}
                onConfirm={() => { setLockedFields(ALL_UNLOCKED); setUnlockPending(false); }}
                title="Rellenar dirección a mano"
                body={<>Si rellenas los campos manualmente, <strong>se perderá la geolocalización</strong> asociada a la dirección autocompletada. Se recomienda usar el buscador siempre que sea posible.</>}
                confirmLabel="Rellenar a mano"
                cancelLabel="Cancelar"
                confirmVariant="warning"
            />
            <Modal show={show} onHide={handleCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>{address ? 'Modificar dirección' : 'Añadir dirección'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Buscar dirección</Form.Label>
                            <AddressAutocompleteInput onSelect={handleSuggestionSelect} />
                        </Form.Group>
                        {Object.values(lockedFields).some(Boolean) && (
                            <div className="mb-3">
                                <Button variant="link" size="sm" className="p-0 text-secondary" onClick={() => setUnlockPending(true)} data-testid="modal-fill-manually-button">
                                    <FaLockOpen className="me-1" />Rellenar a mano
                                </Button>
                            </div>
                        )}
                        <Form.Group className="mb-2">
                            <Form.Label>Dirección</Form.Label>
                            <Form.Control data-testid="modal-address-input" type="text" name="address" value={form.address} onChange={handleChange} placeholder="Ej: Calle Gran Vía, 123" disabled={lockedFields.address} required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Piso, puerta, escalera...</Form.Label>
                            <Form.Control data-testid="modal-address-details-input" type="text" name="addressDetails" value={form.addressDetails} onChange={handleChange} placeholder="Ej: 2º A, Escalera B (opcional)" />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Ciudad</Form.Label>
                            <Form.Control data-testid="modal-city-input" type="text" name="city" value={form.city} onChange={handleChange} placeholder="Introduce ciudad" disabled={lockedFields.city} required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Barrio</Form.Label>
                            <Form.Control data-testid="modal-district-input" type="text" name="district" value={form.district} onChange={handleChange} placeholder="Introduce barrio (opcional)" />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Provincia</Form.Label>
                            <Form.Control data-testid="modal-state-input" type="text" name="state" value={form.state} onChange={handleChange} placeholder="Introduce provincia" disabled={lockedFields.state} required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Código postal</Form.Label>
                            <Form.Control data-testid="modal-zipcode-input" type="text" name="zipCode" value={form.zipCode} onChange={handleChange} placeholder="Introduce código postal" disabled={lockedFields.zipCode} required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>País</Form.Label>
                            <Form.Control data-testid="modal-country-input" type="text" name="country" value={form.country} onChange={handleChange} placeholder="Introduce país" required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Check type="checkbox" label="Marcar como dirección por defecto" name="isDefault" checked={form.isDefault} onChange={handleChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleSubmit} data-testid="modal-save-button">Guardar</Button>
                    <Button variant="secondary" onClick={handleCancel} data-testid="modal-cancel-button">Cancelar</Button>
                </Modal.Footer>
            </Modal>

            <EditCancelModal
                show={showCancelModal}
                onConfirm={() => { setShowCancelModal(false); handleClose(); }}
                onCancel={() => setShowCancelModal(false)}
            />
        </>
    );
};

export default AddAddressModal;
